import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type SyntheticEvent,
} from "react";
import { NEUTRAL_AVATAR_TONE, avatarTone, initialsOf } from "@/lib/avatarTone";

// Circular or square avatar with an initials fallback. Falls back to initials
// when `src` is absent or fails to load (e.g. no avatar uploaded yet).
// Initials get a name-derived pastel tone unless an explicit `tone` is passed.
//
// Размер приходит пропом, но в разметку уходит переменной --av: геометрия
// (ширина, высота, радиус, кегль инициалов) считается в theme.css. Пока она
// стояла инлайном, из стилей до неё было не дотянуться и разделы перебивали
// её через !important.
//
// Пока фото грузится, на его месте серый круг с мягким бликом (.avatar--loading),
// потом фото проявляется (.avatar--fade). Фото, которое браузер уже держит в
// памяти (или отдал из дискового кэша раньше, чем проступил круг), встаёт
// сразу — без блика и без проявления: иначе список, открытый второй раз,
// мигал бы всеми аватарами разом.
//
// Новый адрес у того же человека (имя то же: выбрали другой логотип, сменили
// фото) не прячет уже видную картинку: она стоит, пока новая грузится
// невидимой, и сменяется, когда та готова (не пришла — инициалы). Новый
// человек начинает с чистого листа — чужое фото под его именем хуже серого
// круга.
const HIDDEN: CSSProperties = { display: "none" };

export function Avatar({
  name,
  src,
  size = 28,
  square = false,
  tone,
}: {
  name: string | null;
  src?: string | null;
  size?: number;
  square?: boolean;
  tone?: string;
}) {
  const cur = src ?? null;
  // Состояние относится к конкретному адресу: сменился src — это другая
  // картинка, и прошлое «не загрузилась» или «уже показана» к ней не относится
  // (кроме самой видной картинки, которую держим на время подмены, см. выше).
  const [load, setLoad] = useState<AvatarLoad>(() => ({
    src: cur,
    name,
    phase: "loading",
    keep: null,
  }));
  // Сменились адрес или имя — пересчёт прямо в рендере, до отрисовки: в
  // эффекте первый кадр с новым адресом успел бы спрятать видную картинку.
  let view = load;
  if (load.src !== cur || load.name !== name) {
    view = nextAvatarLoad(load, cur, name);
    setLoad(view);
  }
  const { phase, keep } = view;
  const imgRef = useRef<HTMLImageElement>(null);
  const startedAt = useRef(0);
  // До первой отрисовки: картинка из кэша памяти доступна сразу после
  // установки src (complete + naturalWidth), и тогда заглушку не показываем
  // вовсе. Проверка — в layout-эффекте, чтобы успеть до paint.
  useLayoutEffect(() => {
    startedAt.current = performance.now();
    const img = imgRef.current;
    if (!src || !img) return;
    if (!imageReady(img)) return;
    setLoad((l) =>
      l.src === src && l.phase === "shown" && !l.keep
        ? l
        : { ...l, src, phase: "shown", keep: null },
    );
  }, [src]);
  const onLoad = (e: SyntheticEvent<HTMLImageElement>) => {
    // Пришло раньше, чем проступил серый круг (дисковый кэш — он, в отличие
    // от памяти, отвечает не сразу), — тоже без проявления: круга не было
    // видно, и фото просто встаёт на место. На подмене видной картинки круга
    // не было вовсе.
    const quick = phaseOnLoad(performance.now() - startedAt.current);
    const settle = () =>
      setLoad((l) =>
        l.src !== cur || l.phase !== "loading"
          ? l
          : { ...l, phase: l.keep ? "shown" : quick, keep: null },
      );
    // Подмена: новая показывается уже раскодированной — иначе между старой
    // и новой мог бы проскочить пустой кадр.
    if (keep) e.currentTarget.decode().then(settle, settle);
    else settle();
  };
  const onError = () =>
    setLoad((l) => (l.src !== cur ? l : { ...l, phase: "failed", keep: null }));

  const initials = initialsOf(name);
  const showImg = !!cur && phase !== "failed";
  // Новая картинка грузится на смену уже видной: та остаётся на экране.
  const swapping = showImg && phase === "loading" && keep !== null;
  const fill = tone
    ? { background: tone, color: NEUTRAL_AVATAR_TONE.color }
    : avatarTone(name);

  return (
    <span
      className={
        "avatar" +
        (square ? " avatar--sq" : "") +
        (showImg && phase === "loading" && !swapping ? " avatar--loading" : "") +
        (showImg && phase === "fading" ? " avatar--fade" : "")
      }
      style={
        {
          "--av-size": `${size}px`,
          // Behind a real image — nothing inline (logos are often transparent
          // PNGs; the loading placeholder comes from .avatar--loading); the
          // tinted fallback only shows for initials.
          background: showImg ? undefined : fill.background,
          color: fill.color,
        } as CSSProperties
      }
      title={name ?? undefined}
    >
      {showImg ? (
        <>
          {/* Видная картинка на время подмены — без обработчиков: её
              load/error уже отработали. */}
          {swapping && keep && <img key={keep} src={keep} alt="" />}
          <img
            // Новый адрес — новый элемент: у старого могли остаться и картинка,
            // и запоздалые load/error.
            key={cur}
            ref={imgRef}
            src={cur}
            alt=""
            decoding="async"
            // Грузится на смену видной — невидимо и не занимая места.
            style={swapping ? HIDDEN : undefined}
            onLoad={onLoad}
            onError={onError}
          />
        </>
      ) : (
        <span className="avatar-initials">{initials}</span>
      )}
    </span>
  );
}

/** Догрузилось быстрее — показываем без проявления (см. onLoad в Avatar).
 *  Столько же серый круг ждёт, прежде чем проступить (avatar-ph-in в
 *  theme.css): до этого на месте фото пусто, и проявлять нечего. */
const QUICK_LOAD_MS = 100;

/** Во что переходит догрузившаяся картинка: пришла до серого круга — сразу. */
export function phaseOnLoad(elapsedMs: number): "shown" | "fading" {
  return elapsedMs < QUICK_LOAD_MS ? "shown" : "fading";
}

/** loading — ещё грузится (заглушка); fading — догрузилось на глазах
 *  (проявляется); shown — было готово сразу (без анимации); failed — инициалы. */
export type AvatarPhase = "loading" | "fading" | "shown" | "failed";

/** Состояние аватара: для какого адреса и имени, фаза загрузки и `keep` —
 *  прежняя, уже видная картинка, которая стоит на экране, пока `src`
 *  грузится ей на смену. */
export interface AvatarLoad {
  src: string | null;
  name: string | null;
  phase: AvatarPhase;
  keep: string | null;
}

/** Пришли новые src/name. Тот же адрес — фаза прежняя (имя меняется по
 *  буквам, пока его набирают, и это не повод прятать логотип). Новый адрес у
 *  того же имени — видная картинка остаётся до готовности новой; у другого
 *  имени (другой человек) или когда видно ничего не было — всё сначала. */
export function nextAvatarLoad(
  prev: AvatarLoad,
  src: string | null,
  name: string | null,
): AvatarLoad {
  if (prev.src === src) return { ...prev, name };
  const onScreen =
    prev.phase === "shown" || prev.phase === "fading"
      ? prev.src
      : prev.phase === "loading"
        ? prev.keep
        : null;
  // Вернулись к картинке, которая и так на экране.
  if (src !== null && onScreen === src) return { src, name, phase: "shown", keep: null };
  const keep = src !== null && prev.name === name ? onScreen : null;
  return { src, name, phase: "loading", keep };
}

/** Картинка уже есть целиком: `complete` без `naturalWidth` — это битая. */
export function imageReady(img: { complete: boolean; naturalWidth: number }): boolean {
  return img.complete && img.naturalWidth > 0;
}
